// Margin (plan step 5.4; D52): "how much did we keep this month". Per invoice currency, what was
// paid less refunds, Stripe's fees, taxes and the share of the month's running cost its
// customers used; USD and BRL side by side, never converted. The running cost is the Infra panel's
// month plus the other bills. The owner's inputs (tax rates, the accountant's fee, the month's
// other bills) are set here, each with a reason in the admin audit log.
// Computed daily; "Compute now" refreshes the month after an input changes. Console D's Plan and
// usage (APS-412, admin-d.md "Receita / Margem"): a ledger per currency (USD | BRL), then the
// owner's data and the calibration as `.kv` in the side column, each edit in its form window
// opened from the section's slot or the row it changes.
import { KV, PageHead, Section, Status } from '@apsio/ui'
import { useQuery } from '@tanstack/react-query'
import { useNavigate, useSearch } from '@tanstack/react-router'
import { type ReactNode, useId, useState } from 'react'
import { isApiError } from '../api/client'
import {
  calibrationQuery,
  type Margin,
  marginQuery,
  runMargin,
  setMarginSettings,
  setMonthlyCost,
  useAdminMutation,
} from '../api/queries'
import { ActionForm } from '../components/ActionForm'
import { DataTable, MonthChip, NotConfigured } from '../components/kit'
import { Empty, ErrorState, Loading } from '../components/states'
import { useI18n } from '../i18n'
import { percent, useFormat } from '../lib/format'

type Row = Margin['rows'][number]

/** A form's percent field (15.5) as a rate (0.155); empty is not set. */
const rateOf = (value: FormDataEntryValue | null) =>
  value === null || value === '' ? null : Math.round(Number(value) * 100) / 10_000
/** A form's amount field (400.00) in minor units; empty is not set. */
const minorOf = (value: FormDataEntryValue | null) =>
  value === null || value === '' ? null : Math.round(Number(value) * 100)

/** A warning as a status line: the glyph and a short word in the state's tone, then the sentence. */
export function MoneyWarns({
  items,
  alert,
}: {
  items: { s: 'fire' | 'miss'; text: ReactNode }[]
  /** Announce it (issuing stopped): role alert on the list. */
  alert?: boolean
}) {
  const { t } = useI18n()
  if (items.length === 0) return null
  const list = (
    <ul className="ops-warn">
      {items.map((w, i) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: the warnings keep their order
        <li key={i}>
          {w.s === 'fire' ? (
            <Status s="fire" glyph="×" label={t('money.level.crash')} />
          ) : (
            <Status s="miss" glyph="!" label={t('money.level.warn')} />
          )}
          <span>{w.text}</span>
        </li>
      ))}
    </ul>
  )
  // The alert role on a wrapper: on the list itself it would take the list's role away.
  return alert ? <div role="alert">{list}</div> : list
}

/** The month per currency as a ledger: one line per step, USD and BRL side by side. */
function MarginTable({ rows }: { rows: Row[] }) {
  const { t } = useI18n()
  const { money, n, pct } = useFormat()
  if (rows.length === 0) return <Empty title={t('margin.notComputed')} />
  const none = t('common.none')
  const lines: [string, (r: Row) => ReactNode][] = [
    [t('margin.col.invoices'), (r) => n(r.invoices)],
    [t('margin.col.gross'), (r) => money(r.gross, r.currency)],
    [
      t('margin.col.refunds'),
      (r) => (r.fees_source === 'stripe' ? money(r.refunds, r.currency) : none),
    ],
    [
      t('margin.col.fees'),
      (r) => (r.fees_source === 'stripe' ? money(r.stripe_fees, r.currency) : none),
    ],
    [t('margin.col.taxes'), (r) => money(r.taxes, r.currency)],
    [
      t('margin.col.usage'),
      (r) =>
        r.rate_unavailable ? (
          t('margin.rateUnavailable')
        ) : (
          <>
            {money(r.usage_cost, r.currency)}
            <span className="t2">{pct(r.usage_share)}</span>
          </>
        ),
    ],
    [t('margin.col.net'), (r) => (r.rate_unavailable ? none : money(r.net, r.currency))],
    [
      t('margin.col.margin'),
      (r) => (r.gross > 0 && !r.rate_unavailable ? pct(r.net / r.gross) : none),
    ],
  ]
  return (
    <DataTable
      label={t('table.of', { name: t('margin.tableTitle') })}
      minWidth={0}
      className="money-fit"
    >
      <thead>
        <tr>
          {/* The corner: a header cell, so the head's rule runs the whole width. */}
          <th className="f" scope="col" />
          {rows.map((r, i) => (
            <th
              key={r.currency}
              className={i === rows.length - 1 ? 'num last' : 'num'}
              scope="col"
              translate="no"
            >
              {r.currency.toUpperCase()}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {lines.map(([label, cell]) => (
          <tr key={label}>
            <td className="f wrap">{label}</td>
            {rows.map((r, i) => (
              <td key={r.currency} className={i === rows.length - 1 ? 'num last' : 'num'}>
                {cell(r)}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </DataTable>
  )
}

/**
 * How the table was made: the dollar rate for BRL, the trials' share, where the running cost comes
 * from, the accountant's fee.
 */
function Notes({ data }: { data: Margin }) {
  const { t } = useI18n()
  const { money, pct, brlRate } = useFormat()
  const brl = data.rows.find((r) => r.currency === 'brl')
  return (
    <div className="money-notes">
      <p className="money-note">{t('margin.leadNote')}</p>
      {brl && brl.fx_rate !== null ? (
        <p className="money-note">{t('margin.rate', { rate: brlRate(brl.fx_rate) })}</p>
      ) : null}
      <p className="money-note">
        {t('margin.trials', {
          share: pct(data.trials.usage_share),
          cost: money(data.trials.usage_cost_usd, 'usd'),
        })}
      </p>
      <p className="money-note">{t('margin.costParts')}</p>
      <p className="money-note">{t('margin.accountantNote')}</p>
      <p className="money-note">{t('margin.schedule')}</p>
    </div>
  )
}

/** The month's warnings, the one thing on this page to act on first. */
function warnings(t: ReturnType<typeof useI18n>['t'], data: Margin) {
  const brl = data.rows.find((r) => r.currency === 'brl')
  return [
    ...(data.alert ? [{ s: 'fire' as const, text: t('margin.alert') }] : []),
    ...(data.rows.some((r) => r.fees_source === 'unavailable')
      ? [{ s: 'miss' as const, text: t('margin.feesUnavailable') }]
      : []),
    ...(brl && brl.fx_rate === null ? [{ s: 'miss' as const, text: t('margin.noRate') }] : []),
  ]
}

/**
 * The owner's inputs as facts: Edit in the section's slot opens the rates and the fee, Set on the
 * month's cost row opens that one, each in its form window with a reason (audited).
 */
function Inputs({ data }: { data: Margin }) {
  const { t } = useI18n()
  const { money, pct, month } = useFormat()
  const settings = useAdminMutation(setMarginSettings, [['margin']])
  // The other bills are part of the calibration's actual cost too.
  const cost = useAdminMutation(setMonthlyCost, [
    ['margin', data.month],
    ['calibration', data.month],
  ])
  const [open, setOpen] = useState<'settings' | 'cost' | null>(null)
  const ids = {
    domestic: useId(),
    export: useId(),
    accountant: useId(),
    cost: useId(),
    note: useId(),
  }
  const { inputs } = data
  const rate = (r: number | null) => (r === null ? t('margin.notSet') : pct(r, 2))
  const named = month(data.month)
  return (
    <Section
      title={t('margin.inputsTitle')}
      meta={t('margin.inputsSub')}
      right={
        <button
          type="button"
          className="btn ghost sm"
          aria-label={t('margin.editSettings')}
          aria-expanded={open === 'settings'}
          onClick={() => setOpen(open === 'settings' ? null : 'settings')}
        >
          {t('margin.edit')}
        </button>
      }
    >
      <ActionForm
        label={t('margin.editSettings')}
        confirm={t('margin.save')}
        open={open === 'settings'}
        onClose={() => setOpen(null)}
        onSubmit={(reason, form) =>
          settings.mutateAsync({
            domesticTaxRate: rateOf(form.get('domestic')),
            exportTaxRate: rateOf(form.get('export')),
            accountantMonthlyBrl: minorOf(form.get('accountant')),
            reason,
          })
        }
      >
        <div className="field-d wide">
          <label className="lbl" htmlFor={ids.domestic}>
            {t('margin.domesticTaxPercent')}
          </label>
          <input
            id={ids.domestic}
            name="domestic"
            className="input mono"
            type="number"
            min={0}
            max={50}
            step={0.01}
            defaultValue={
              inputs.domestic_tax_rate === null ? '' : +(inputs.domestic_tax_rate * 100).toFixed(2)
            }
          />
        </div>
        <div className="field-d wide">
          <label className="lbl" htmlFor={ids.export}>
            {t('margin.exportTaxPercent')}
          </label>
          <input
            id={ids.export}
            name="export"
            className="input mono"
            type="number"
            min={0}
            max={50}
            step={0.01}
            defaultValue={
              inputs.export_tax_rate === null ? '' : +(inputs.export_tax_rate * 100).toFixed(2)
            }
          />
        </div>
        <div className="field-d wide">
          <label className="lbl" htmlFor={ids.accountant}>
            {t('margin.accountantBrl')}
          </label>
          <input
            id={ids.accountant}
            name="accountant"
            className="input mono"
            type="number"
            min={0}
            step={0.01}
            defaultValue={
              inputs.accountant_monthly_brl === null ? '' : inputs.accountant_monthly_brl / 100
            }
          />
        </div>
      </ActionForm>
      <dl className="kv" aria-label={t('margin.inputsTitle')}>
        <dt>{t('margin.domesticTax')}</dt>
        <dd>{rate(inputs.domestic_tax_rate)}</dd>
        <dt>{t('margin.exportTax')}</dt>
        <dd>{rate(inputs.export_tax_rate)}</dd>
        <dt>{t('margin.accountant')}</dt>
        <dd>
          {inputs.accountant_monthly_brl === null
            ? t('margin.notSet')
            : money(inputs.accountant_monthly_brl, 'brl')}
        </dd>
        <dt>{t('margin.cost', { month: named })}</dt>
        <dd className="money-row-act">
          {inputs.other_bills_usd === null
            ? t('margin.notSet')
            : money(inputs.other_bills_usd, 'usd')}
          <button
            type="button"
            className="btn ghost sm"
            aria-label={t('margin.editCost', { month: named })}
            aria-expanded={open === 'cost'}
            onClick={() => setOpen(open === 'cost' ? null : 'cost')}
          >
            {t('margin.set')}
          </button>
        </dd>
        {inputs.note ? (
          <>
            <dt>{t('margin.note')}</dt>
            <dd className="stack">{inputs.note}</dd>
          </>
        ) : null}
      </dl>
      <ActionForm
        label={t('margin.editCost', { month: named })}
        confirm={t('margin.save')}
        open={open === 'cost'}
        onClose={() => setOpen(null)}
        onSubmit={(reason, form) =>
          cost.mutateAsync({
            month: data.month,
            otherBillsUsd: minorOf(form.get('cost')) ?? 0,
            note: String(form.get('note') ?? '').trim() || null,
            reason,
          })
        }
      >
        <div className="field-d wide">
          <label className="lbl" htmlFor={ids.cost}>
            {t('margin.costUsd')}
          </label>
          <input
            id={ids.cost}
            name="cost"
            className="input mono"
            type="number"
            min={0}
            step={0.01}
            required
            aria-describedby={`${ids.cost}-hint`}
            defaultValue={inputs.other_bills_usd === null ? '' : inputs.other_bills_usd / 100}
          />
          <span className="hint" id={`${ids.cost}-hint`}>
            {t('margin.costHint')}
          </span>
        </div>
        <div className="field-d wide">
          <label className="lbl" htmlFor={ids.note}>
            {t('margin.note')}
          </label>
          <input
            id={ids.note}
            name="note"
            className="input"
            maxLength={500}
            defaultValue={inputs.note ?? ''}
          />
        </div>
      </ActionForm>
      <p className="money-note">{t('margin.inputsNote')}</p>
    </Section>
  )
}

/**
 * D109's calibration: what the cost model says the month's usage cost against the bills
 * (Northflank, ClickHouse Cloud and Cloudflare as the Infra panel read them, plus the other
 * bills), each part as a row of how it was computed. Works with billing off.
 */
function Calibration({ month }: { month: string }) {
  const { t, number } = useI18n()
  const { usd, n } = useFormat()
  const c = useQuery(calibrationQuery(month))
  return (
    <Section title={t('margin.calibration.title')} meta={t('margin.calibration.sub')}>
      {c.isPending ? (
        <Loading />
      ) : c.isError ? (
        <ErrorState error={c.error} retry={() => c.refetch()} />
      ) : (
        <>
          <MoneyWarns
            items={c.data.alert ? [{ s: 'fire', text: t('margin.calibration.alert') }] : []}
          />
          <KV
            label={t('margin.calibration.title')}
            items={[
              [t('margin.calibration.predicted'), usd(c.data.predicted.total)],
              [t('margin.calibration.fixed'), usd(c.data.predicted.fixed)],
              [
                t('margin.calibration.usage', { sessions: n(c.data.captured_sessions) }),
                usd(c.data.predicted.usage),
              ],
              [t('margin.calibration.actual'), usd(c.data.actual.total)],
              [t('margin.calibration.infra'), usd(c.data.actual.infra)],
              [
                t('margin.calibration.other'),
                c.data.actual.other === null ? t('common.none') : usd(c.data.actual.other),
              ],
              [
                t('margin.calibration.gap'),
                c.data.gap === null
                  ? t('margin.calibration.noActual')
                  : percent(number, c.data.gap, 1, 'exceptZero'),
              ],
            ]}
          />
          <p className="money-note">{t('margin.calibration.note', { version: c.data.version })}</p>
        </>
      )}
    </Section>
  )
}

export function MarginPage() {
  const { t, dateTime } = useI18n()
  const search = useSearch({ strict: false }) as { month?: string }
  const navigate = useNavigate()
  const month = search.month ?? new Date().toISOString().slice(0, 7)
  const margin = useQuery(marginQuery(month))
  const run = useAdminMutation(runMargin, [['margin', month]])
  const billingOff = margin.isError && isApiError(margin.error, 503)
  return (
    <main id="main" className="pg">
      <PageHead
        title={t('margin.title')}
        meta={t('margin.lede')}
        actions={
          <>
            <MonthChip
              value={month}
              onChange={(m) => navigate({ to: '/margin', search: { month: m } })}
            />
            {margin.isSuccess ? (
              <button
                type="button"
                className="btn sm"
                disabled={run.isPending}
                onClick={() => run.mutate({ month })}
              >
                {t('margin.runNow')}
              </button>
            ) : null}
          </>
        }
      />
      <div className="grid2">
        <div className="sections">
          <Section
            title={t('margin.tableTitle')}
            meta={
              margin.data?.computed_at
                ? t('margin.computedAt', { time: dateTime(margin.data.computed_at) })
                : undefined
            }
          >
            {run.isError ? <ErrorState error={run.error} /> : null}
            {margin.isPending ? (
              <Loading />
            ) : billingOff ? (
              <NotConfigured missing={t('margin.billingOff')} task={t('margin.billingOffTask')} />
            ) : margin.isError ? (
              <ErrorState error={margin.error} retry={() => margin.refetch()} />
            ) : (
              <>
                <MoneyWarns items={warnings(t, margin.data)} />
                <MarginTable rows={margin.data.rows} />
                <Notes data={margin.data} />
              </>
            )}
          </Section>
        </div>
        <div className="sections">
          {margin.isSuccess ? <Inputs data={margin.data} /> : null}
          <Calibration month={month} />
        </div>
      </div>
    </main>
  )
}
